import React, { useState, useEffect } from 'react';
import { DialogContent, DialogContentText, Button, Grid } from '@material-ui/core';

import { StyledDialog, CurrencyDisplay, useFormEvents, FormHub } from '@lib/common';
import {
  ReservationCharge,
  ReservationForms,
  FormStatus,
  RefundReservationChargeModel,
  Reservation,
  RefundForm,
} from '@lib/state';
import { ReservationRefundForm } from './reservation-refund.form';
import { JournalAccount } from 'app/state';

interface Props {
  reservation: Reservation;
  charge: ReservationCharge;
  accounts: JournalAccount[];
  onSubmit?: (model: RefundReservationChargeModel) => void;
}

export const ReservationRefundModal: React.FC<Props> = ({
  reservation,
  charge,
  accounts,
  onSubmit,
}) => {
  const [{ status }] = useFormEvents(ReservationForms.RefundReservationCharge);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (status === FormStatus.Success) setOpen(false);
  }, [status]);

  if (!onSubmit)
    return (
      <>
        {charge.refundableAmount <= 0 && (
          <Button size="small" color="secondary" disabled>
            Refunded
          </Button>
        )}
      </>
    );

  return (
    <>
      <Button
        size="small"
        color="secondary"
        onClick={() => setOpen(true)}
        disabled={charge.refundableAmount <= 0}
      >
        {charge.refundableAmount <= 0 ? 'Refunded' : 'Refund'}
      </Button>
      <StyledDialog title="Refund" open={open} onClose={() => setOpen(false)} maxWidth={'md'}>
        <DialogContent>
          <DialogContentText>
            Please enter the amounts and accounts to be refunded.
          </DialogContentText>
          <Grid container>
            <Grid container item xs={12}>
              <Grid item xs={4}>
                <DialogContentText>
                  Total Charge Amount: <CurrencyDisplay value={charge.totalAmount} />
                </DialogContentText>
              </Grid>

              <Grid item xs={4}>
                <DialogContentText>
                  Room Revenue Amount: <CurrencyDisplay value={charge.amount} />
                </DialogContentText>
              </Grid>
              <Grid item xs={4}>
                <DialogContentText>
                  Tax Amount: <CurrencyDisplay value={charge.tax.total} />
                </DialogContentText>
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={4}>
                <DialogContentText>
                  Refundable Amount Remaining: <CurrencyDisplay value={charge.refundableAmount} />
                </DialogContentText>
              </Grid>
              <Grid item xs={4}>
                <DialogContentText>
                  Refundable Room Revenue Remaining:
                  <CurrencyDisplay value={charge.refundableRoomRevenueRemaining} />
                </DialogContentText>
              </Grid>

              <Grid item xs={4}>
                <DialogContentText>
                  Refundable Tax Remaining:
                  <CurrencyDisplay value={charge.refundableTaxRemaining?.total} />
                </DialogContentText>
              </Grid>
            </Grid>
          </Grid>

          <FormHub
            onSubmit={({ chargeId, description, refundAccounts, taxAccounts }: RefundForm) =>
              // map from form model to request model
              onSubmit({
                chargeId,
                description,
                refundAccounts: [...refundAccounts, ...(taxAccounts || [])]
                  .filter(({ amount }) => !!amount && parseFloat(amount) > 0)
                  .map(({ account, amount }) => ({ account, amount: parseFloat(amount || '0') })),
              })
            }
          >
            <ReservationRefundForm reservation={reservation} charge={charge} accounts={accounts} />
          </FormHub>
        </DialogContent>
      </StyledDialog>
    </>
  );
};
